/**
 * Purchase Order Detail Page.
 *
 * Formatted like a paper purchase order form with a header block,
 * an editable line items table with inline add row, and a totals
 * section at the bottom showing subtotal, shipping, tax, and total.
 */

import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Check,
  Package,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Calendar,
  Truck,
  AlertTriangle,
  CheckCircle2,
  Clock,
  DollarSign,
  Info,
  Mail,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import AssetNameBuilder from "@/components/ui/AssetNameBuilder";
import CategorySelect from "@/components/ui/CategorySelect";
import CalcTrigger from "@/components/ui/CalcTrigger";
import PaymentPanel from "@/features/payments/PaymentPanel";
import {
  usePurchaseOrder,
  useAddPOLine,
  useDeletePOLine,
  useSubmitPO,
  useReceivePO,
  useReceiveLines,
  useUpdatePOLine,
  useUpdatePO,
  useVendors,
  useCreateVendor,
  useAddLineCharge,
  useDeleteLineCharge,
} from "@/features/procurement/api";
import { toast } from "@/stores/toastStore";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Card from "@/components/ui/Card";
import DocumentHeader from "@/components/ui/DocumentHeader";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import PrintButton from "@/components/ui/PrintButton";
import { useOrgConfig } from "@/features/settings/api";
import { generatePOHtml } from "@/utils/printTemplates";
import ComboSelect from "@/components/ui/ComboSelect";
import ComposeEmail from "@/components/ui/ComposeEmail";
import InlineEdit from "@/components/ui/InlineEdit";
import LineDetailPanel from "@/components/ui/LineDetailPanel";
import {
  AddChargeRow,
  ChargeSubRow,
  type ChargeRowLayout,
} from "@/components/ui/ChargeSubRow";
import TaxDrawer from "@/components/ui/TaxDrawer";
import {
  effectiveTaxRate,
  formatRatePct,
  taxSource,
  TAX_CHIP_CLASS,
} from "@/utils/taxDisplay";
import { useBusinessLocations } from "@/features/locations/api";
import type { PurchaseOrder, Vendor } from "@/types/purchase-order";

const statusVariant: Record<
  string,
  "default" | "success" | "warning" | "danger" | "info"
> = {
  Draft: "default",
  Submitted: "info",
  "In-Transit": "warning",
  Received: "success",
  Cancelled: "danger",
};

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    v,
  );

/**
 * Inline-editable category cell.
 *
 * Displays the category as static text. Double-clicking activates a
 * CategorySelect dropdown with autocomplete, existing category selection,
 * and the ability to create new categories — the same behavior as the
 * category controls used elsewhere in the application.
 */
function InlineCategoryCell({
  value,
  editable,
  onSave,
}: {
  value: string;
  editable: boolean;
  onSave: (val: string) => void;
}) {
  const [editing, setEditing] = useState(false);

  if (!editable) {
    return (
      <span className="text-xs capitalize text-[var(--text-secondary)]">
        {value || <span className="text-[var(--text-tertiary)]">—</span>}
      </span>
    );
  }

  if (!editing) {
    return (
      <span
        onDoubleClick={() => setEditing(true)}
        className="cursor-pointer rounded-[var(--radius-sm)] px-1 -mx-1 transition-colors hover:bg-[var(--surface-sunken)] text-xs capitalize text-[var(--text-secondary)]"
        title="Double-click to edit"
      >
        {value || (
          <span className="text-[var(--text-tertiary)] italic">
            Select category
          </span>
        )}
      </span>
    );
  }

  return (
    <CategorySelect
      value={value}
      onChange={(val) => {
        if (val !== value) onSave(val);
        setEditing(false);
      }}
    />
  );
}
/**
 * Where a charge row's content sits within this table's columns.
 *
 * The PO table is: [receive?] # | Description | Category | Qty | Unit | Amount |
 * actions. A charge row spans Description+Category, leaves Qty and Unit empty,
 * and puts its amount in the Amount column.
 */
function chargeRowLayout(hasReceiveCol: boolean): ChargeRowLayout {
  return {
    leading: hasReceiveCol ? 2 : 1,
    contentColSpan: 2,
    filler: 2,
    trailing: 1,
  };
}

/**
 * Editable document header for Draft/Submitted POs.
 *
 * Mirrors the DocumentHeader layout but renders vendor and date fields
 * as inline-editable values that activate on double-click.
 */
function EditableDocumentHeader({
  po,
  vendors,
  updatePOMutation,
  locations,
}: {
  po: PurchaseOrder;
  vendors: Vendor[];
  updatePOMutation: ReturnType<typeof useUpdatePO>;
  locations: Array<{
    id: string;
    name: string;
    address: Record<string, string> | null;
    phone: string | null;
    email: string | null;
  }>;
}) {
  const { data: config } = useOrgConfig();
  const qc = useQueryClient();
  const biz = config?.business;

  // Resolve the selected location's address for the company block.
  const selectedLocation = locations.find((l) => l.id === po.location_id);
  const addr = selectedLocation?.address
    ? {
        street1: selectedLocation.address.street1,
        street2: selectedLocation.address.street2,
        city: selectedLocation.address.city,
        state: selectedLocation.address.state,
        zip: selectedLocation.address.zip,
      }
    : biz?.primary_address;
  const contactPhone = selectedLocation?.phone || biz?.phone;
  const contactEmail = selectedLocation?.email || biz?.email;
  const [editingVendor, setEditingVendor] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);
  const createVendorMutation = useCreateVendor();

  // Look up the selected vendor to get address details.
  const selectedVendor = vendors.find((v) => v.id === po.vendor_id);
  const vendorAddress = selectedVendor?.address || {};

  // Local vendor detail draft for editing (address + contact info).
  const [detailDraft, setDetailDraft] = useState<{
    address: Record<string, string>;
    phone: string;
    email: string;
    website: string;
  }>({ address: {}, phone: "", email: "", website: "" });

  // Sync draft when vendor changes.
  useEffect(() => {
    if (!editingAddress && selectedVendor) {
      setDetailDraft({
        address: selectedVendor.address || {},
        phone: selectedVendor.phone || "",
        email: selectedVendor.email || "",
        website: selectedVendor.website || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVendor?.id, editingAddress]);

  const updateAddrField = (field: string, value: string) => {
    setDetailDraft((prev) => ({
      ...prev,
      address: { ...prev.address, [field]: value },
    }));
  };

  const saveDetailsToVendor = async () => {
    if (!po.vendor_id) return;
    try {
      await apiClient.put(`/api/v1/vendors/${po.vendor_id}`, {
        address: detailDraft.address,
        phone: detailDraft.phone || null,
        email: detailDraft.email || null,
        website: detailDraft.website || null,
      });
      qc.invalidateQueries({ queryKey: ["vendors"] });
      qc.invalidateQueries({ queryKey: ["purchase-orders", po.id] });
      toast.success("Vendor details saved");
      setEditingAddress(false);
    } catch {
      toast.error("Failed to save vendor details");
    }
  };

  const hasAddress =
    vendorAddress.street1 || vendorAddress.city || vendorAddress.state;
  const hasContactInfo =
    selectedVendor?.phone || selectedVendor?.email || selectedVendor?.website;

  const fmtDate = (d: string | null | undefined) =>
    d
      ? new Date(d).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : null;

  return (
    <div className="rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-base)] mb-6 overflow-hidden print:border-0 print:mb-4">
      <div className="h-1 bg-gradient-to-r from-brand-500 via-brand-400 to-accent-400" />
      <div className="p-5">
        {/* Row 1: Company info + Document info */}
        <div className="flex justify-between gap-6">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-[var(--text-primary)]">
              {biz?.company_name || "Your Company"}
            </h2>
            {/* Location selector */}
            {locations.length > 1 && (
              <div className="mt-0.5">
                <select
                  value={po.location_id || ""}
                  onChange={(e) =>
                    updatePOMutation.mutate({
                      location_id: e.target.value || null,
                    })
                  }
                  className="h-6 rounded-[var(--radius-sm)] border border-[var(--border-subtle)] bg-transparent px-1 text-xs text-[var(--color-interactive-primary)] cursor-pointer focus:outline-none"
                >
                  <option value="">Primary Location</option>
                  {locations.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {addr?.street1 && (
              <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                {addr.street1}
                {addr.street2 ? `, ${addr.street2}` : ""}
              </p>
            )}
            {addr?.city && (
              <p className="text-xs text-[var(--text-secondary)]">
                {addr.city}, {addr.state} {addr.zip}
              </p>
            )}
            <div className="flex items-center gap-3 mt-1 text-xs text-[var(--text-tertiary)]">
              {contactPhone && <span>{contactPhone}</span>}
              {contactEmail && <span>{contactEmail}</span>}
              {biz?.website && <span>{biz.website}</span>}
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[var(--text-tertiary)]">
              Purchase Order
            </p>
            <p className="text-xl font-bold font-mono text-brand-600 mt-0.5">
              {po.po_number}
            </p>
            <div className="mt-2 space-y-0.5 text-xs text-[var(--text-secondary)]">
              <p>
                Date:{" "}
                <InlineEdit
                  value={po.order_date ? po.order_date.slice(0, 10) : ""}
                  displayValue={fmtDate(po.order_date) || undefined}
                  onSave={(val) =>
                    updatePOMutation.mutate({
                      order_date: val || null,
                    })
                  }
                  type="date"
                  placeholder="Set date"
                  className="font-medium text-[var(--text-primary)] inline"
                  inputClassName="w-36"
                />
              </p>
              <p>
                Expected Delivery:{" "}
                <InlineEdit
                  value={
                    po.expected_delivery_date
                      ? po.expected_delivery_date.slice(0, 10)
                      : ""
                  }
                  displayValue={fmtDate(po.expected_delivery_date) || undefined}
                  onSave={(val) =>
                    updatePOMutation.mutate({
                      expected_delivery_date: val || null,
                    })
                  }
                  type="date"
                  placeholder="Set date"
                  className="font-medium text-[var(--text-primary)] inline"
                  inputClassName="w-36"
                />
              </p>
              <div className="mt-1">
                <Badge variant={statusVariant[po.status] || "default"}>
                  {po.status}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Vendor — mirrors company info block layout */}
        <div className="mt-4 pt-4 border-t border-[var(--border-subtle)]">
          <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[var(--text-tertiary)] mb-1">
            Vendor
          </p>

          {/* Vendor name */}
          {editingVendor ? (
            <div className="max-w-xs">
              <ComboSelect
                options={vendors.map((v) => ({
                  value: v.id,
                  label: v.name,
                }))}
                value={po.vendor_id || ""}
                onChange={(val) => {
                  if (val && val !== po.vendor_id) {
                    updatePOMutation.mutate({ vendor_id: val });
                  }
                  setEditingVendor(false);
                }}
                placeholder="Select vendor..."
                creatable
                onCreate={async (name) => {
                  try {
                    const vendor = await createVendorMutation.mutateAsync({
                      name,
                    });
                    updatePOMutation.mutate({ vendor_id: vendor.id });
                    setEditingVendor(false);
                  } catch {
                    toast.error("Failed to create vendor");
                  }
                }}
              />
            </div>
          ) : (
            <p
              className={`text-sm font-semibold cursor-pointer rounded-[var(--radius-sm)] px-1 -mx-1 hover:bg-[var(--surface-sunken)] transition-colors ${
                po.vendor_name
                  ? "text-[var(--text-primary)]"
                  : "text-[var(--text-tertiary)] italic"
              }`}
              onDoubleClick={() => setEditingVendor(true)}
              title="Double-click to edit"
            >
              {po.vendor_name || "Double-click to select vendor"}
            </p>
          )}

          {/* Vendor address + contact (shown when vendor is selected) */}
          {po.vendor_id && !editingVendor && (
            <>
              {editingAddress ? (
                <div className="mt-1.5 space-y-1.5 max-w-sm">
                  <input
                    type="text"
                    value={detailDraft.address.street1 || ""}
                    onChange={(e) => updateAddrField("street1", e.target.value)}
                    placeholder="Street address"
                    className="block w-full h-7 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
                    autoFocus
                  />
                  <input
                    type="text"
                    value={detailDraft.address.street2 || ""}
                    onChange={(e) => updateAddrField("street2", e.target.value)}
                    placeholder="Suite, unit, etc."
                    className="block w-full h-7 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
                  />
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={detailDraft.address.city || ""}
                      onChange={(e) => updateAddrField("city", e.target.value)}
                      placeholder="City"
                      className="flex-1 h-7 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
                    />
                    <input
                      type="text"
                      value={detailDraft.address.state || ""}
                      onChange={(e) => updateAddrField("state", e.target.value)}
                      placeholder="State"
                      className="w-16 h-7 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
                    />
                    <input
                      type="text"
                      value={detailDraft.address.zip || ""}
                      onChange={(e) => updateAddrField("zip", e.target.value)}
                      placeholder="ZIP"
                      className="w-20 h-7 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
                    />
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={detailDraft.phone}
                      onChange={(e) =>
                        setDetailDraft((p) => ({
                          ...p,
                          phone: e.target.value,
                        }))
                      }
                      placeholder="Phone"
                      className="flex-1 h-7 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
                    />
                    <input
                      type="email"
                      value={detailDraft.email}
                      onChange={(e) =>
                        setDetailDraft((p) => ({
                          ...p,
                          email: e.target.value,
                        }))
                      }
                      placeholder="Email"
                      className="flex-1 h-7 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
                    />
                  </div>
                  <input
                    type="text"
                    value={detailDraft.website}
                    onChange={(e) =>
                      setDetailDraft((p) => ({
                        ...p,
                        website: e.target.value,
                      }))
                    }
                    placeholder="Website"
                    className="block w-full h-7 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
                  />
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      type="button"
                      onClick={saveDetailsToVendor}
                      className="text-[11px] font-medium text-[var(--color-interactive-primary)] hover:underline"
                    >
                      Save to Vendor
                    </button>
                    <span className="text-[var(--text-tertiary)]">·</span>
                    <button
                      type="button"
                      onClick={() => setEditingAddress(false)}
                      className="text-[11px] text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="cursor-pointer rounded-[var(--radius-sm)] px-1 -mx-1 hover:bg-[var(--surface-sunken)] transition-colors"
                  onDoubleClick={() => setEditingAddress(true)}
                  title="Double-click to edit vendor details"
                >
                  {hasAddress ? (
                    <>
                      {vendorAddress.street1 && (
                        <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                          {vendorAddress.street1}
                          {vendorAddress.street2
                            ? `, ${vendorAddress.street2}`
                            : ""}
                        </p>
                      )}
                      {vendorAddress.city && (
                        <p className="text-xs text-[var(--text-secondary)]">
                          {vendorAddress.city}, {vendorAddress.state}{" "}
                          {vendorAddress.zip}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-[var(--text-tertiary)] italic mt-0.5">
                      Add vendor address
                    </p>
                  )}
                  {hasContactInfo ? (
                    <div className="flex items-center gap-3 mt-1 text-xs text-[var(--text-tertiary)]">
                      {selectedVendor?.phone && (
                        <span>{selectedVendor.phone}</span>
                      )}
                      {selectedVendor?.email && (
                        <span>{selectedVendor.email}</span>
                      )}
                      {selectedVendor?.website && (
                        <span>{selectedVendor.website}</span>
                      )}
                    </div>
                  ) : !hasAddress ? null : (
                    <p className="text-xs text-[var(--text-tertiary)] italic mt-1">
                      Add contact info
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PurchaseOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: po, isLoading, error: poError } = usePurchaseOrder(id);
  const { data: config } = useOrgConfig();
  const addLineMutation = useAddPOLine(id || "");
  const deleteLineMutation = useDeletePOLine(id || "");
  const submitMutation = useSubmitPO(id || "");
  const receiveMutation = useReceivePO(id || "");
  const receiveLinesMutation = useReceiveLines(id || "");
  const updateLineMutation = useUpdatePOLine(id || "");
  const updatePOMutation = useUpdatePO(id || "");
  const { data: vendorData } = useVendors();
  const { data: businessLocations } = useBusinessLocations();

  const [showEmail, setShowEmail] = useState(false);
  const [showTaxDrawer, setShowTaxDrawer] = useState(false);
  const [expandedLineId, setExpandedLineId] = useState<string | null>(null);
  const [detailPanelLineId, setDetailPanelLineId] = useState<string | null>(
    null,
  );
  const addChargeMutation = useAddLineCharge(id || "");
  const deleteChargeMutation = useDeleteLineCharge(id || "");
  const [selectedReceiveIds, setSelectedReceiveIds] = useState<Set<string>>(
    new Set(),
  );

  const qc = useQueryClient();

  const vendors = useMemo(() => vendorData?.items || [], [vendorData?.items]);

  // Inline add row state.
  const [showAddLine, setShowAddLine] = useState(false);
  const [newDesc, setNewDesc] = useState("");
  const [newAssetName, setNewAssetName] = useState("");
  const [newCategory, setNewCategory] = useState("equipment");
  const [newQty, setNewQty] = useState("1");
  const [newPrice, setNewPrice] = useState("");
  const [newFields, setNewFields] = useState<{
    year?: string;
    make?: string;
    model?: string;
    variant?: string;
  }>({});
  const [newCustomFields, setNewCustomFields] = useState<
    Record<string, string>
  >({});

  const handleAddLine = async () => {
    if (!newAssetName.trim() || !newPrice) return;
    try {
      await addLineMutation.mutateAsync({
        description: newDesc,
        asset_name: newAssetName || undefined,
        category: newCategory,
        quantity: parseInt(newQty) || 1,
        unit_price: parseFloat(newPrice),
        year: newFields.year ? parseInt(newFields.year) : undefined,
        make: newFields.make || undefined,
        model: newFields.model || undefined,
        variant: newFields.variant || undefined,
        custom_fields:
          Object.keys(newCustomFields).length > 0 ? newCustomFields : undefined,
      });
      setNewDesc("");
      setNewAssetName("");
      setNewQty("1");
      setNewPrice("");
      setNewFields({});
      setNewCustomFields({});
      setShowAddLine(false);
      toast.success("Item added");
    } catch {
      toast.error("Failed to add item");
    }
  };

  if (isLoading) return <LoadingSpinner className="py-24" size="lg" />;
  if (!po) {
    const errMsg =
      (poError as { response?: { data?: { detail?: string } } })?.response?.data
        ?.detail ||
      (poError as Error)?.message ||
      "Purchase order not found.";
    return (
      <div className="py-24 text-center">
        <p className="text-[var(--text-secondary)]">{errMsg}</p>
        {poError && (
          <p className="text-xs text-[var(--text-tertiary)] mt-2">
            If you recently updated the application, you may need to run
            database migrations.
          </p>
        )}
        <Button
          variant="secondary"
          className="mt-4"
          onClick={() => navigate("/procurement")}
        >
          Back
        </Button>
      </div>
    );
  }

  const canEdit = po.status === "Draft" || po.status === "Submitted";
  const canSubmit =
    po.status === "Draft" && po.line_items.length > 0 && !!po.vendor_id;
  const canReceive =
    po.status === "Submitted" ||
    po.status === "In-Transit" ||
    po.status === "Partially Received";
  const hasUnreceivedLines = po.line_items.some(
    (l) => l.line_status !== "received",
  );
  const lineStatusIcon = (status: string) => {
    switch (status) {
      case "received":
        return (
          <CheckCircle2 className="h-3.5 w-3.5 text-[var(--text-success)]" />
        );
      case "on_order":
        return <Clock className="h-3.5 w-3.5 text-blue-500" />;
      case "shipped":
        return <Truck className="h-3.5 w-3.5 text-violet-500" />;
      case "cancelled":
        return (
          <AlertTriangle className="h-3.5 w-3.5 text-[var(--text-danger)]" />
        );
      default:
        return <Clock className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />;
    }
  };
  const toggleReceiveSelect = (lineId: string) => {
    setSelectedReceiveIds((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  };
  const handleReceiveSelected = async () => {
    if (selectedReceiveIds.size === 0) return;
    try {
      await receiveLinesMutation.mutateAsync(Array.from(selectedReceiveIds));
      setSelectedReceiveIds(new Set());
      toast.success(
        "Items received",
        `${selectedReceiveIds.size} item(s) marked as received.`,
      );
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      toast.error("Failed to receive items", msg || "Please try again.");
    }
  };

  return (
    <div className="space-y-6 page-content">
      {/* ═══════════════════════════════════════════════════════
          PO DOCUMENT — styled like a paper purchase order form
          ═══════════════════════════════════════════════════════ */}
      <Card padding="none">
        {/* ── Top bar: navigation + actions ── */}
        <div className="border-b border-[var(--border-default)] px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => navigate("/procurement")}
              className="text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] flex items-center gap-1 text-xs"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </button>
            <Badge variant={statusVariant[po.status] || "default"}>
              {po.status}
            </Badge>
          </div>

          {/* Actions */}
          <div className="mt-4 flex flex-wrap gap-2">
            {canSubmit && (
              <Button
                size="sm"
                onClick={() =>
                  submitMutation.mutate(undefined, {
                    onSuccess: () =>
                      toast.success(
                        "PO submitted",
                        "Assets are now tracked as On Order.",
                      ),
                    onError: () => toast.error("Submit failed"),
                  })
                }
                isLoading={submitMutation.isPending}
              >
                <Check className="h-4 w-4" /> Submit
              </Button>
            )}
            {canReceive && hasUnreceivedLines && (
              <Button
                size="sm"
                onClick={() =>
                  receiveMutation.mutate(undefined, {
                    onSuccess: () => toast.success("All items received"),
                    onError: (err: unknown) => {
                      const msg = (
                        err as { response?: { data?: { detail?: string } } }
                      )?.response?.data?.detail;
                      toast.error("Receive failed", msg || "Please try again.");
                    },
                  })
                }
                isLoading={receiveMutation.isPending}
              >
                <Package className="h-4 w-4" /> Receive All
              </Button>
            )}
            {canReceive && selectedReceiveIds.size > 0 && (
              <Button
                size="sm"
                variant="secondary"
                onClick={handleReceiveSelected}
                isLoading={receiveLinesMutation.isPending}
              >
                <Package className="h-4 w-4" /> Receive Selected (
                {selectedReceiveIds.size})
              </Button>
            )}
            {canEdit && po.line_items.length > 0 && (
              <Button
                size="sm"
                variant="secondary"
                onClick={async () => {
                  try {
                    const { data } = await apiClient.post(
                      `/api/v1/purchase-orders/${id}/apply-schemes`,
                    );
                    qc.invalidateQueries({ queryKey: ["purchase-orders", id] });
                    const s = data?.summary ?? {};
                    const parts: string[] = [];
                    if (Number(s.line_charges_created) > 0) {
                      parts.push(
                        `${s.line_charges_created} line fee${
                          Number(s.line_charges_created) === 1 ? "" : "s"
                        } (${fmt(Number(s.line_fees_total || 0))})`,
                      );
                    }
                    if (Number(s.order_tax_total) > 0) {
                      parts.push(`${fmt(Number(s.order_tax_total))} order tax`);
                    }
                    toast.success(
                      "Schemes applied",
                      parts.length
                        ? `Applied ${parts.join(" + ")}.`
                        : "No matching schemes for this order.",
                    );
                  } catch {
                    toast.error("Failed to apply schemes");
                  }
                }}
              >
                <DollarSign className="h-4 w-4" /> Apply Schemes
              </Button>
            )}
            <PrintButton
              getHtml={() => generatePOHtml(po, config?.business)}
              label="Print"
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowEmail(true)}
            >
              <Mail className="h-4 w-4" /> Email PO
            </Button>
          </div>

          {/* Draft editing banner */}
          {po.status === "Draft" && (
            <div className="mt-3 rounded-[var(--radius-md)] bg-[var(--fill-info)] px-4 py-2.5">
              <p className="text-xs text-[var(--text-info)]">
                <span className="font-semibold">Draft mode</span> — Double-click
                any field to edit it. You can change the vendor, dates, line
                items, shipping, tax, and notes. When ready, click{" "}
                <strong>Submit</strong> to finalize.
              </p>
            </div>
          )}
        </div>

        {/* ── Document header with inline-editable fields in Draft mode ── */}
        {(() => {
          // Resolve the location address for the document header.
          const poLocation = businessLocations?.find(
            (l) => l.id === po.location_id,
          );
          const locAddr = poLocation
            ? {
                name: poLocation.name,
                street1: poLocation.address?.street1,
                street2: poLocation.address?.street2,
                city: poLocation.address?.city,
                state: poLocation.address?.state,
                zip: poLocation.address?.zip,
                phone: poLocation.phone || undefined,
                email: poLocation.email || undefined,
              }
            : undefined;

          return canEdit ? (
            <EditableDocumentHeader
              po={po}
              vendors={vendors}
              updatePOMutation={updatePOMutation}
              locations={businessLocations || []}
            />
          ) : (
            <DocumentHeader
              documentType="Purchase Order"
              documentNumber={po.po_number}
              status={po.status}
              statusVariant={statusVariant[po.status] || "default"}
              date={po.order_date}
              dueDate={po.expected_delivery_date}
              dueDateLabel="Expected Delivery"
              party={po.vendor_name ? { name: po.vendor_name } : undefined}
              extra={{
                Currency: po.currency !== "USD" ? po.currency : undefined,
              }}
              locationAddress={locAddr}
            />
          );
        })()}

        {/* ── Line items table ── */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border-default)] bg-[var(--surface-sunken)]">
                {canReceive && <th className="w-8 px-2"></th>}
                <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] w-12">
                  #
                </th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                  Description
                </th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                  Category
                </th>
                <th className="px-4 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] w-16">
                  Qty
                </th>
                <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] w-28">
                  Unit price
                </th>
                <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] w-28">
                  Amount
                </th>
                <th className="w-16"></th>
              </tr>
            </thead>
            <tbody>
              {po.line_items.map((line, i) => {
                const isExpanded = expandedLineId === line.id;
                const isOverdue =
                  line.expected_delivery_date &&
                  new Date(line.expected_delivery_date) < new Date() &&
                  line.line_status !== "received";
                return (
                  <React.Fragment key={line.id}>
                    <tr
                      className={`border-b border-[var(--border-subtle)] hover:bg-[var(--surface-base)] transition-colors ${isOverdue ? "bg-red-50/30 dark:bg-red-900/5" : ""}`}
                    >
                      {/* Receive checkbox */}
                      {canReceive && (
                        <td className="px-2 py-2.5 w-8">
                          {line.line_status !== "received" ? (
                            <input
                              type="checkbox"
                              checked={selectedReceiveIds.has(line.id)}
                              onChange={() => toggleReceiveSelect(line.id)}
                              className="rounded border-[var(--border-default)]"
                            />
                          ) : null}
                        </td>
                      )}
                      <td className="px-3 py-2.5 w-8">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-[var(--text-tertiary)] tabular-nums">
                            {i + 1}
                          </span>
                          {lineStatusIcon(line.line_status)}
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <InlineEdit
                          value={line.asset_name || line.description}
                          onSave={(val) =>
                            updateLineMutation.mutate({
                              lineId: line.id,
                              data: line.asset_name
                                ? { asset_name: val }
                                : { description: val },
                            })
                          }
                          editable={canEdit && line.line_status !== "received"}
                          className="font-medium text-[var(--text-primary)]"
                          inputClassName="w-full"
                        />
                        {line.asset_name &&
                          line.description &&
                          !line.asset_name
                            .toLowerCase()
                            .startsWith(line.description.toLowerCase()) && (
                            <InlineEdit
                              value={line.description}
                              onSave={(val) =>
                                updateLineMutation.mutate({
                                  lineId: line.id,
                                  data: { description: val },
                                })
                              }
                              editable={
                                canEdit && line.line_status !== "received"
                              }
                              className="text-xs text-[var(--text-tertiary)]"
                              inputClassName="w-full"
                            />
                          )}
                        {line.asset_id && (
                          <Link
                            to={`/inventory/${line.asset_id}`}
                            className="text-[10px] text-[var(--color-interactive-primary)] hover:underline"
                          >
                            View asset →{" "}
                            {line.line_status === "on_order" && (
                              <span className="text-blue-500">(On Order)</span>
                            )}
                          </Link>
                        )}
                        {isOverdue && (
                          <span className="text-[10px] text-red-600 flex items-center gap-0.5 mt-0.5">
                            <AlertTriangle className="h-3 w-3" /> Overdue —
                            expected{" "}
                            {new Date(
                              line.expected_delivery_date!,
                            ).toLocaleDateString()}
                          </span>
                        )}
                        {!isOverdue &&
                          line.expected_delivery_date &&
                          po.expected_delivery_date &&
                          line.expected_delivery_date.slice(0, 10) !==
                            po.expected_delivery_date.slice(0, 10) && (
                            <span className="text-[10px] text-[var(--text-tertiary)] flex items-center gap-0.5 mt-0.5">
                              <Calendar className="h-3 w-3" /> Expected{" "}
                              {new Date(
                                line.expected_delivery_date,
                              ).toLocaleDateString()}
                            </span>
                          )}
                      </td>
                      <td className="px-4 py-2.5 text-xs capitalize text-[var(--text-secondary)]">
                        <InlineCategoryCell
                          value={line.category}
                          editable={canEdit && line.line_status !== "received"}
                          onSave={(val) =>
                            updateLineMutation.mutate({
                              lineId: line.id,
                              data: { category: val },
                            })
                          }
                        />
                      </td>
                      <td className="px-4 py-2.5 text-center tabular-nums text-[var(--text-primary)]">
                        <InlineEdit
                          value={String(line.quantity)}
                          onSave={(val) => {
                            const qty = parseInt(val) || 1;
                            if (qty !== line.quantity) {
                              updateLineMutation.mutate({
                                lineId: line.id,
                                data: { quantity: qty },
                              });
                            }
                          }}
                          type="number"
                          min="1"
                          editable={canEdit && line.line_status !== "received"}
                          className="tabular-nums text-[var(--text-primary)]"
                          inputClassName="w-16 text-center"
                        />
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-[var(--text-secondary)]">
                        <InlineEdit
                          value={String(Number(line.unit_price))}
                          displayValue={fmt(Number(line.unit_price))}
                          onSave={(val) => {
                            const price = parseFloat(val) || 0;
                            if (price !== Number(line.unit_price)) {
                              updateLineMutation.mutate({
                                lineId: line.id,
                                data: { unit_price: price },
                              });
                            }
                          }}
                          type="number"
                          step="0.01"
                          min="0"
                          editable={canEdit && line.line_status !== "received"}
                          className="tabular-nums text-[var(--text-secondary)]"
                          inputClassName="w-24 text-right"
                        />
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <span className="tabular-nums font-medium text-[var(--text-primary)]">
                          {fmt(Number(line.total_price))}
                        </span>
                        {(line.charges?.length > 0 ||
                          Number(line.line_fees) > 0) && (
                          <div className="text-[10px] text-[var(--text-warning)] mt-0.5">
                            +{" "}
                            {fmt(
                              Number(line.line_fees || 0) +
                                (line.charges?.reduce(
                                  (s, c) => s + Number(c.amount),
                                  0,
                                ) || 0),
                            )}{" "}
                            charges
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2.5">
                        <div className="flex items-center gap-1">
                          {/* Expand charges */}
                          <button
                            onClick={() =>
                              setExpandedLineId(isExpanded ? null : line.id)
                            }
                            className={`flex items-center justify-center h-7 w-7 rounded-[var(--radius-sm)] transition-colors ${
                              isExpanded
                                ? "bg-[var(--color-interactive-primary)]/10 text-[var(--color-interactive-primary)]"
                                : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-base)]"
                            }`}
                            title={
                              isExpanded ? "Collapse charges" : "Expand charges"
                            }
                          >
                            {isExpanded ? (
                              <ChevronUp className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronDown className="h-3.5 w-3.5" />
                            )}
                          </button>
                          {/* Open detail panel */}
                          <button
                            onClick={() => setDetailPanelLineId(line.id)}
                            className="flex items-center justify-center h-7 w-7 rounded-[var(--radius-sm)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-base)] transition-colors"
                            title="Line details"
                          >
                            <Info className="h-3.5 w-3.5" />
                          </button>
                          {canEdit && line.line_status !== "received" && (
                            <button
                              onClick={() =>
                                deleteLineMutation.mutate(line.id, {
                                  onSuccess: () =>
                                    toast.success("Item removed"),
                                  onError: () =>
                                    toast.error("Failed to remove"),
                                })
                              }
                              className="flex h-7 w-7 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-tertiary)] hover:text-[var(--text-danger)] hover:bg-[var(--fill-danger)] transition-colors"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Charge sub-rows */}
                    {isExpanded && (
                      <>
                        {(line.charges || []).map((charge) => (
                          <ChargeSubRow
                            key={charge.id}
                            charge={charge}
                            layout={chargeRowLayout(canReceive)}
                            canDelete={
                              canEdit && line.line_status !== "received"
                            }
                            onDelete={() =>
                              deleteChargeMutation.mutate(
                                { lineId: line.id, chargeId: charge.id },
                                {
                                  onSuccess: () =>
                                    toast.success("Charge removed"),
                                  onError: () =>
                                    toast.error("Failed to remove charge"),
                                },
                              )
                            }
                          />
                        ))}

                        {/* Add charge row */}
                        {canEdit && line.line_status !== "received" && (
                          <AddChargeRow
                            layout={chargeRowLayout(canReceive)}
                            onAdd={(data) =>
                              addChargeMutation.mutate(
                                { lineId: line.id, ...data },
                                {
                                  onSuccess: () =>
                                    toast.success("Charge added"),
                                  onError: () =>
                                    toast.error("Failed to add charge"),
                                },
                              )
                            }
                            isAdding={addChargeMutation.isPending}
                          />
                        )}
                      </>
                    )}
                  </React.Fragment>
                );
              })}

              {/* ── Inline add row — single-row aligned with table columns ── */}
              {canEdit && showAddLine && (
                <tr className="border-b border-[var(--border-subtle)] bg-[var(--surface-sunken)]/50">
                  {canReceive && <td className="w-8" />}
                  <td className="px-3 py-2.5 w-8" />
                  <td className="px-4 py-2.5" colSpan={2}>
                    <AssetNameBuilder
                      value={newAssetName}
                      onChange={(name) => {
                        setNewAssetName(name);
                        if (!newDesc) setNewDesc(name);
                      }}
                      onFieldsChange={(fields) => {
                        setNewFields(fields);
                        const parts = [
                          fields.year,
                          fields.make,
                          fields.model,
                          fields.variant,
                        ]
                          .filter(Boolean)
                          .join(" ");
                        if (parts) setNewDesc(parts);
                      }}
                      onCategoryChange={setNewCategory}
                      onCustomFieldsChange={setNewCustomFields}
                      category={newCategory}
                      compact
                    />
                  </td>
                  <td className="px-4 py-2.5 w-16">
                    <input
                      type="number"
                      min="1"
                      value={newQty}
                      onChange={(e) => setNewQty(e.target.value)}
                      className="block w-full h-8 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-sm text-center text-[var(--text-primary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </td>
                  <td className="px-4 py-2.5 w-28">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newPrice}
                      onChange={(e) => setNewPrice(e.target.value)}
                      placeholder="0.00"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddLine();
                        }
                      }}
                      className="block w-full h-8 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-sm text-right text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </td>
                  <td className="px-4 py-2.5 w-28 text-right">
                    <span className="text-sm tabular-nums text-[var(--text-tertiary)]">
                      {newPrice
                        ? fmt(
                            (parseInt(newQty) || 1) *
                              (parseFloat(newPrice) || 0),
                          )
                        : "—"}
                    </span>
                  </td>
                  <td className="px-2 py-2.5 w-16">
                    <button
                      onClick={handleAddLine}
                      disabled={
                        !newAssetName.trim() ||
                        !newPrice ||
                        addLineMutation.isPending
                      }
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-sm)] bg-[var(--color-interactive-primary)] text-white disabled:opacity-40 hover:opacity-90 transition-opacity"
                    >
                      {addLineMutation.isPending ? (
                        <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      ) : (
                        <Plus className="h-4 w-4" />
                      )}
                    </button>
                  </td>
                </tr>
              )}

              {/* Add Line Item button */}
              {canEdit && !showAddLine && (
                <tr>
                  <td
                    colSpan={canReceive ? 9 : 8}
                    className="px-4 py-3 text-center"
                  >
                    <button
                      type="button"
                      onClick={() => setShowAddLine(true)}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--color-interactive-primary)] hover:underline"
                    >
                      <Plus className="h-3.5 w-3.5" /> Add Line Item
                    </button>
                  </td>
                </tr>
              )}

              {/* Empty state */}
              {po.line_items.length === 0 && !canEdit && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-8 text-center text-sm text-[var(--text-tertiary)]"
                  >
                    No line items
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── Totals section (right-aligned, like a paper form) ── */}
        <div className="border-t border-[var(--border-default)] px-6 py-4">
          <div className="flex justify-end">
            <div className="w-64 space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[var(--text-secondary)]">Subtotal</span>
                <span className="tabular-nums font-medium text-[var(--text-primary)]">
                  {fmt(Number(po.subtotal))}
                </span>
              </div>
              {Number(po.fees_total) > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[var(--text-warning)]">Fees</span>
                  <span className="tabular-nums text-[var(--text-warning)]">
                    {fmt(Number(po.fees_total))}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-[var(--text-secondary)] flex items-center gap-1">
                  Shipping
                  {Number(po.shipping_cost) > 0 && (
                    <CalcTrigger
                      mode="shipping"
                      prefill={{
                        totalShipping: String(po.shipping_cost),
                        items: String(po.line_items.length),
                      }}
                      source={po.po_number}
                    />
                  )}
                </span>
                <InlineEdit
                  value={String(Number(po.shipping_cost))}
                  displayValue={fmt(Number(po.shipping_cost))}
                  onSave={(val) => {
                    const v = parseFloat(val) || 0;
                    if (v !== Number(po.shipping_cost)) {
                      updatePOMutation.mutate({ shipping_cost: v });
                    }
                  }}
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  editable={canEdit}
                  className="tabular-nums text-[var(--text-primary)]"
                  inputClassName="w-24 text-right"
                />
              </div>
              {(() => {
                const lines = po.tax_lines || [];
                // A PO has neither an order discount nor taxable charges, so
                // its base is just the subtotal.
                const rate = effectiveTaxRate(lines, {
                  subtotal: Number(po.subtotal),
                });
                const src = taxSource(lines);
                return (
                  <button
                    type="button"
                    onClick={() => setShowTaxDrawer(true)}
                    title="View tax breakdown and options"
                    className="group flex w-full items-center justify-between gap-2 rounded-[var(--radius-sm)] -mx-1 px-1 py-0.5 text-sm transition-colors hover:bg-[var(--surface-sunken)]"
                  >
                    <span className="flex min-w-0 items-center gap-1.5 text-[var(--text-secondary)]">
                      Tax
                      {rate != null && rate > 0 && (
                        <span className="text-xs text-[var(--text-tertiary)]">
                          ({formatRatePct(rate)})
                        </span>
                      )}
                      {src && src.tone !== "auto" && (
                        <span
                          className={`inline-flex items-center rounded-full px-1.5 py-0 text-[10px] font-medium ${TAX_CHIP_CLASS[src.tone]}`}
                        >
                          {src.label}
                        </span>
                      )}
                    </span>
                    <span className="flex shrink-0 items-center gap-0.5 tabular-nums text-[var(--text-primary)]">
                      {fmt(Number(po.tax))}
                      <ChevronRight className="h-3.5 w-3.5 text-[var(--text-tertiary)] opacity-0 transition-opacity group-hover:opacity-100" />
                    </span>
                  </button>
                );
              })()}
              <div className="border-t border-[var(--border-default)] pt-1.5 mt-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-[var(--text-primary)]">
                    Total
                  </span>
                  <span className="text-lg tabular-nums font-bold text-[var(--text-primary)]">
                    {fmt(Number(po.total))}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Notes ── */}
        <div className="border-t border-[var(--border-default)] px-6 py-4">
          <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] mb-1">
            Notes
          </p>
          <InlineEdit
            value={po.notes || ""}
            onSave={(val) => {
              if (val !== (po.notes || "")) {
                updatePOMutation.mutate({ notes: val || null });
              }
            }}
            type="textarea"
            placeholder="Add notes about this purchase order..."
            editable={canEdit}
            className="text-sm text-[var(--text-secondary)]"
            inputClassName="w-full"
          />
        </div>
      </Card>

      {/* Tax slide-over — opened from the Tax line in the Subtotals section */}
      <TaxDrawer
        isOpen={showTaxDrawer}
        onClose={() => setShowTaxDrawer(false)}
        entityType="purchase_order"
        entityId={po.id}
        taxLines={po.tax_lines || []}
        basis={{ subtotal: Number(po.subtotal) }}
        taxTotal={Number(po.tax)}
        canEdit={canEdit}
        queryKey={["purchase-orders", id!]}
      />

      {/* ── Payments — only show after PO has been submitted/received ── */}
      {po.status !== "Draft" && (
        <div>
          <h3 className="text-sm font-medium text-[var(--text-primary)] mb-3">
            Payments
          </h3>
          <PaymentPanel
            entityType="purchase_order"
            entityId={po.id}
            lineItems={po.line_items}
          />
        </div>
      )}

      <ComposeEmail
        isOpen={showEmail}
        onClose={() => setShowEmail(false)}
        defaultSubject={"Purchase Order " + po.po_number}
        entityType="purchase_order"
        entityId={po.id}
      />

      {/* Line detail slide-out panel */}
      {detailPanelLineId &&
        (() => {
          const panelLine = po.line_items.find(
            (l) => l.id === detailPanelLineId,
          );
          if (!panelLine) return null;
          return (
            <LineDetailPanel
              line={panelLine}
              poExpectedDate={po.expected_delivery_date}
              poId={po.id}
              isOpen
              onClose={() => setDetailPanelLineId(null)}
              canEdit={canEdit}
              orderTaxLines={po.tax_lines}
              onUpdateLine={(data) =>
                updateLineMutation.mutate({
                  lineId: panelLine.id,
                  data,
                })
              }
              onAddCharge={(data) =>
                addChargeMutation.mutate(
                  { lineId: panelLine.id, ...data },
                  {
                    onSuccess: () => toast.success("Charge added"),
                    onError: () => toast.error("Failed to add charge"),
                  },
                )
              }
              onDeleteCharge={(chargeId) =>
                deleteChargeMutation.mutate(
                  { lineId: panelLine.id, chargeId },
                  {
                    onSuccess: () => toast.success("Charge removed"),
                    onError: () => toast.error("Failed to remove charge"),
                  },
                )
              }
              isAddingCharge={addChargeMutation.isPending}
              onReceiveLine={
                canReceive && panelLine.line_status !== "received"
                  ? () =>
                      receiveLinesMutation.mutate([panelLine.id], {
                        onSuccess: () => {
                          toast.success(
                            `${panelLine.asset_name || panelLine.description} received`,
                          );
                          setDetailPanelLineId(null);
                        },
                        onError: (err: unknown) => {
                          const msg = (
                            err as {
                              response?: { data?: { detail?: string } };
                            }
                          )?.response?.data?.detail;
                          toast.error(
                            "Failed to receive",
                            msg || "Please try again.",
                          );
                        },
                      })
                  : undefined
              }
              isReceiving={receiveLinesMutation.isPending}
            />
          );
        })()}
    </div>
  );
}

export default PurchaseOrderDetailPage;
